import { Article } from '../../components/Layout'
import { H1, H2, P, Lead, UL, LI, Callout, Code, CodeBlock, Steps, Step, Screenshot } from '../../components/DocComponents'

const toc = [
  { id: 'what', title: 'What is a Drinker?', level: 2 as const },
  { id: 'writing', title: 'Writing a Drinker', level: 2 as const },
  { id: 'context', title: 'Available context', level: 2 as const },
  { id: 'create', title: 'Creating a Drinker', level: 2 as const },
  { id: 'use-in-plate', title: 'Using a Drinker in a Plate', level: 2 as const },
]

export function Drinkers() {
  return (
    <Article toc={toc}>
      <H1>Drinkers (Scripts)</H1>
      <Lead>A Drinker is a JavaScript function that queries your data and returns a structured result. It is the bridge between raw database records and what a Plate View displays.</Lead>

      <H2 id="what">What is a Drinker?</H2>
      <P>Drinkers run server-side inside Brainer. They receive the current input parameters as context and can call any Brainer API or query the database directly. They return a plain object whose keys become the <Code>resultKey</Code> options for Plate Views.</P>
      <Callout type="info">Drinkers are sandboxed JavaScript — they cannot access the filesystem, make network calls outside Brainer, or import external modules.</Callout>

      <H2 id="writing">Writing a Drinker</H2>
      <CodeBlock language="javascript" title="Example Drinker — fetch open invoices for a customer">
{`// Available globals: context (input params), brainer (API client)
const { customerId, page = 1 } = context;

if (!customerId) return { invoices: [], total: 0 };

const result = await brainer.findPaginated('Invoice', {
  page: Number(page),
  limit: 25,
  filters: [{ field: 'Client.id', op: 'eq', value: customerId }],
  orderBy: [{ field: 'created_at', dir: 'DESC' }],
});

return {
  invoices: result.Invoice,
  total: result.total,
  totalPages: result.totalPages,
};`}
      </CodeBlock>
      <P>The return value is a plain object. Each key (<Code>invoices</Code>, <Code>total</Code>, <Code>totalPages</Code>) becomes available as a <Code>resultKey</Code> on any Plate View that uses this Drinker.</P>

      <H2 id="context">Available context</H2>
      <UL>
        <LI><Code>context</Code> — An object with all current input parameter values keyed by param name</LI>
        <LI><Code>brainer</Code> — A client with methods: <Code>findPaginated(cupTitle, options)</Code>, <Code>findById(id)</Code>, <Code>findChildren(parentId, childCupTitle)</Code></LI>
      </UL>
      <Callout type="tip">
        For simple list/single/children views, prefer the <strong>Coffee API mode</strong> in the Plate View wizard — it avoids writing a Drinker entirely and is more performant. Drinkers shine for complex cross-entity aggregations and computed values.
      </Callout>

      <H2 id="create">Creating a Drinker</H2>
      <Steps>
        <Step n={1} title="Open Drinkers">Go to <strong>Brainer › Drinkers</strong> → <strong>New Drinker</strong>.</Step>
        <Step n={2} title="Write the script">Use the built-in code editor. The editor supports basic autocompletion for the <Code>brainer</Code> client.</Step>
        <Step n={3} title="Test">Click <strong>Run</strong> with sample input params to see the output in the console below the editor.</Step>
        <Step n={4} title="Save">The Drinker is now available to any Plate View in the same business.</Step>
      </Steps>
      <Screenshot alt="Drinker editor with JavaScript code on the left and JSON output on the right" caption="The test output panel shows the full return value, making it easy to find the right resultKey to use." />

      <H2 id="use-in-plate">Using a Drinker in a Plate</H2>
      <P>In the Plate View wizard, choose <strong>Drinker</strong> as the data source, select your drinker from the list, and enter the <Code>resultKey</Code> (the key in the returned object that holds the array or value you want to display).</P>
      <P>Run the drinker with sample inputs to discover the available keys automatically.</P>
    </Article>
  )
}
